import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { weighPhotoDisplaySrc } from "../lib/localScaleAgent";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";

/** Resolve s3:// / HTTPS photo refs for <img> (same pattern as weigh evidence). */
export function GatePhotoThumb({ src, label, className = "" }) {
  const [href, setHref] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    const raw = String(src || "").trim();
    if (!raw) {
      setHref("");
      return undefined;
    }
    if (/^https?:\/\//i.test(raw) && !/X-Amz-/i.test(raw)) {
      setHref(raw);
      return undefined;
    }
    if (/^blob:/i.test(raw)) {
      setHref(raw);
      return undefined;
    }
    if (raw.startsWith("media://") || raw.includes("/weighbridge/media/")) {
      setHref(weighPhotoDisplaySrc(raw));
      return undefined;
    }
    api
      .get("/weighbridge/photo-url", { params: { uri: raw.split("?")[0] } })
      .then(({ data }) => {
        if (!cancelled) setHref(weighPhotoDisplaySrc(data?.url || "") || data?.url || "");
      })
      .catch(() => {
        if (!cancelled) {
          setHref("");
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  if (failed) {
    return <p className="text-[10px] text-muted-foreground">{label}: unavailable</p>;
  }
  if (!href) {
    return <p className="text-[10px] text-muted-foreground">{label}: loading…</p>;
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={`block min-w-0 ${className}`}>
      <img
        src={href}
        alt={label}
        className="h-24 w-full object-cover rounded border border-border bg-muted"
        onError={() => setFailed(true)}
      />
      {label ? <span className="text-[10px] text-muted-foreground">{label}</span> : null}
    </a>
  );
}

/**
 * Local file pickers for check-in: one number-plate + multiple vehicle images.
 * Parent uploads via uploadGatePhotos() before creating the pass.
 */
export function GatePassPhotoFields({
  plateFile,
  onPlateFile,
  vehicleFiles,
  onVehicleFiles,
  testIdPrefix = "gate",
  className = "",
}) {
  const platePreview = React.useMemo(
    () => (plateFile ? URL.createObjectURL(plateFile) : ""),
    [plateFile],
  );
  const vehiclePreviews = React.useMemo(
    () => (vehicleFiles || []).map((f) => URL.createObjectURL(f)),
    [vehicleFiles],
  );

  useEffect(() => {
    return () => {
      if (platePreview) URL.revokeObjectURL(platePreview);
    };
  }, [platePreview]);

  useEffect(() => {
    return () => {
      vehiclePreviews.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [vehiclePreviews]);

  return (
    <div className={`space-y-3 rounded-md border border-border bg-muted/20 p-3 ${className}`} data-testid={`${testIdPrefix}-photos`}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Gate photos</p>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Number plate
        </Label>
        <Input
          type="file"
          accept="image/*"
          capture="environment"
          className="mt-1.5"
          onChange={(e) => onPlateFile(e.target.files?.[0] || null)}
          data-testid={`${testIdPrefix}-plate-input`}
        />
        {platePreview ? (
          <div className="mt-2 relative inline-block max-w-[160px]">
            <img src={platePreview} alt="Number plate preview" className="h-20 w-auto object-cover rounded border border-border" />
            <button
              type="button"
              className="absolute -top-1.5 -right-1.5 rounded-full bg-card border border-border p-0.5"
              onClick={() => onPlateFile(null)}
              aria-label="Remove plate photo"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ) : null}
      </div>
      <div>
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Vehicle images (multiple)
        </Label>
        <Input
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="mt-1.5"
          onChange={(e) => {
            const next = Array.from(e.target.files || []);
            onVehicleFiles([...(vehicleFiles || []), ...next].slice(0, 20));
            e.target.value = "";
          }}
          data-testid={`${testIdPrefix}-vehicle-photos-input`}
        />
        {vehiclePreviews.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {vehiclePreviews.map((src, i) => (
              <div key={`${src}-${i}`} className="relative">
                <img src={src} alt={`Vehicle ${i + 1}`} className="h-16 w-20 object-cover rounded border border-border" />
                <button
                  type="button"
                  className="absolute -top-1.5 -right-1.5 rounded-full bg-card border border-border p-0.5"
                  onClick={() => onVehicleFiles((vehicleFiles || []).filter((_, idx) => idx !== i))}
                  aria-label={`Remove vehicle photo ${i + 1}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <p className="text-[10px] text-muted-foreground mt-1">Optional. Capture plate and vehicle angles with the phone camera.</p>
      </div>
    </div>
  );
}

/** Upload local files to S3 via /gate/upload-photo; returns fields for gate-pass create. */
export async function uploadGatePhotos({ plateFile, vehicleFiles, entityCode = "pending" }) {
  let number_plate_snapshot_uri;
  const vehicle_photos = [];
  const code = String(entityCode || "pending").trim() || "pending";

  if (plateFile) {
    const form = new FormData();
    form.append("file", plateFile);
    form.append("kind", "number_plate");
    form.append("entity_code", code);
    const { data } = await api.post("/gate/upload-photo", form);
    number_plate_snapshot_uri = data.uri;
  }

  for (const f of vehicleFiles || []) {
    const form = new FormData();
    form.append("file", f);
    form.append("kind", "vehicle");
    form.append("entity_code", code);
    const { data } = await api.post("/gate/upload-photo", form);
    vehicle_photos.push({
      uri: data.uri,
      url: data.url,
      captured_at: data.captured_at,
      uploaded_by: data.uploaded_by,
      label: "vehicle",
    });
  }

  return { number_plate_snapshot_uri, vehicle_photos };
}

/**
 * Gallery + add-more controls for an existing in-yard (or OUT) gate pass.
 */
export default function GatePassPhotosPanel({
  gatePass,
  canUpload = false,
  onUpdated,
  testId = "gate-pass-photos",
}) {
  const [busy, setBusy] = useState(false);
  if (!gatePass) return null;

  const plateUri = gatePass.number_plate_snapshot_uri;
  const photos = Array.isArray(gatePass.vehicle_photos) ? gatePass.vehicle_photos : [];
  const inYard = String(gatePass.status || "").toUpperCase() === "IN";
  const allow = canUpload && inYard;

  const uploadOne = async (file, kind) => {
    if (!file || !gatePass.id) return;
    try {
      setBusy(true);
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      await api.post(`/gate-passes/${gatePass.id}/photos`, form);
      toast.success(kind === "number_plate" ? "Number plate photo saved" : "Vehicle photo added");
      onUpdated?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-[10px] uppercase tracking-widest text-muted-foreground">Gate photos</h4>
        {allow ? (
          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
            <Camera className="h-3 w-3" /> Add while in yard
          </span>
        ) : null}
      </div>

      <div>
        <p className="text-[10px] text-muted-foreground mb-1">Number plate</p>
        {plateUri ? (
          <div className="max-w-[200px]">
            <GatePhotoThumb src={plateUri} label="Number plate" />
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">No number plate photo yet.</p>
        )}
        {allow ? (
          <div className="mt-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {plateUri ? "Replace plate photo" : "Add plate photo"}
            </Label>
            <Input
              type="file"
              accept="image/*"
              capture="environment"
              className="mt-1.5"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadOne(f, "number_plate");
              }}
              data-testid={`${testId}-plate-add`}
            />
          </div>
        ) : null}
      </div>

      <div>
        <p className="text-[10px] text-muted-foreground mb-1">Vehicle images ({photos.length})</p>
        {photos.length === 0 ? (
          <p className="text-xs text-muted-foreground">No vehicle photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {photos.map((ph, i) => (
              <GatePhotoThumb
                key={ph.uri || ph.url || i}
                src={ph.uri || ph.url}
                label={ph.label || `Vehicle ${i + 1}`}
              />
            ))}
          </div>
        )}
        {allow ? (
          <div className="mt-2">
            <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Add vehicle photo(s)</Label>
            <Input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="mt-1.5"
              disabled={busy}
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                e.target.value = "";
                for (const f of files) {
                  await uploadOne(f, "vehicle");
                }
              }}
              data-testid={`${testId}-vehicle-add`}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
